import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, homeFor } from "../context/AuthContext";
import { fmtErr } from "../api";
import { AuthLayout } from "./Login";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { User, Building2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "individual", company: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register(form);
      navigate(homeFor(u));
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Create your account" subtitle="Start a service request in minutes">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-error">{error}</p>}
        <RadioGroup value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))} className="grid grid-cols-2 gap-3">
          <Label htmlFor="role-individual" data-testid="role-individual-option"
            className={`flex cursor-pointer items-center gap-2.5 rounded-lg border p-3.5 text-sm font-medium ${form.role === "individual" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600"}`}>
            <RadioGroupItem value="individual" id="role-individual" className="sr-only" />
            <User className="h-4 w-4" /> Individual
          </Label>
          <Label htmlFor="role-builder" data-testid="role-builder-option"
            className={`flex cursor-pointer items-center gap-2.5 rounded-lg border p-3.5 text-sm font-medium ${form.role === "builder_admin" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600"}`}>
            <RadioGroupItem value="builder_admin" id="role-builder" className="sr-only" />
            <Building2 className="h-4 w-4" /> Builder / Developer
          </Label>
        </RadioGroup>
        <div className="space-y-1.5">
          <Label htmlFor="name">{form.role === "builder_admin" ? "Contact person name" : "Full name"}</Label>
          <Input id="name" required data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        {form.role === "builder_admin" && (
          <div className="space-y-1.5">
            <Label htmlFor="company">Company / Firm name</Label>
            <Input id="company" required data-testid="register-company-input" value={form.company} onChange={set("company")} placeholder="ABC Developers Pvt. Ltd." />
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required data-testid="register-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" data-testid="register-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 98XXXXXXXX" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required minLength={6} data-testid="register-password-input" value={form.password} onChange={set("password")} placeholder="Min. 6 characters" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
          {loading ? "Creating account…" : "Create account"}
        </Button>
        <p className="text-center text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-blue-600 hover:underline" data-testid="register-login-link">Log in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
